// Phase 9.1.B — Client portal email verification consumer.
//
// Reads ?token=... from the URL (placed there by the SendGrid email link)
// and submits to POST /portal/auth/verify-email. On success, the server
// auto-issues a session — we pop the access_token + user into AuthProvider
// and navigate to /dashboard. 404 means the link expired or was already
// used; show a resend CTA.
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, type PortalUser } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";

type VerifyState = "verifying" | "success" | "invalid";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, setSession } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>("verifying");
  const ran = useRef(false);

  useEffect(() => {
    // React StrictMode mounts twice in dev — only fire once.
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("invalid");
      return;
    }
    (async () => {
      try {
        const result = await api<{
          access_token: string;
          user: PortalUser;
        }>("/portal/auth/verify-email", {
          method: "POST",
          body: JSON.stringify({ token }),
          skipAuth: true,
        });
        setSession(result.access_token, result.user);
        setState("success");
        // Brief celebratory pause, then redirect.
        setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setState("invalid");
        } else {
          setState("invalid");
        }
      }
    })();
  }, [token, setSession, navigate]);

  if (user && state !== "success") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
      <div className="w-full max-w-md text-center">
        {state === "verifying" && (
          <>
            <div className="mb-6 inline-flex h-16 w-16 animate-pulse items-center justify-center rounded-md bg-peach-400" />
            <h1 className="mb-2 font-display text-3xl font-bold tracking-tight">
              Verifying your email…
            </h1>
            <p className="text-brown-400">One moment.</p>
          </>
        )}

        {state === "success" && (
          <>
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-md bg-peach-400">
              <span className="text-2xl font-bold text-brown-500">✓</span>
            </div>
            <h1 className="mb-2 font-display text-3xl font-bold tracking-tight">
              You're in
            </h1>
            <p className="text-brown-400">Taking you to your dashboard…</p>
          </>
        )}

        {state === "invalid" && (
          <>
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-md bg-rust-100">
              <span className="text-2xl font-bold text-rust-500">!</span>
            </div>
            <h1 className="mb-2 font-display text-3xl font-bold tracking-tight">
              Link expired
            </h1>
            <p className="mb-8 text-brown-400">
              That verification link is no longer valid. Links work once and
              expire 24 hours after signup — if you already verified, just
              sign in.
            </p>
            <p className="text-sm text-brown-400">
              <Link
                to="/login"
                className="font-semibold text-ink underline-offset-2 hover:underline"
              >
                Go to sign-in
              </Link>{" "}
              and use "Resend verification email" to get a fresh link.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
